import React, { FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers'
import { DeprecatedControlledTextInput } from 'transfix-form/components/ControlledTextInput/ControlledTextInput'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { FormModal } from 'transfix-ui/components/Modals'
import { RangeDatePicker } from 'transfix-ui/components/RangeDatePicker'
import Box from 'transfix-ui/core/Box'
import {
  ShipperStrikePriceOverride,
  useCreateShipperStrikePriceOverride,
  useUpdateShipperStrikePriceOverride,
} from 'apps/marketplace/apiTypes/shipperStrikePriceOverrides'
import { ShipperStrikePrices } from 'apps/marketplace/apiTypes/shipperStrikePrices'
import {
  StrikePriceOverrideSchema,
  IStrikePriceOverrideSchema,
} from '../schema/StrikePriceOverrideSchema'
import {
  formatDate,
  correctForTimeZone,
  percentageToFloat,
  floatToPercentage,
} from '../utils/shipperStrikePrice'

interface IOverrideModalProps {
  onClose: () => void
  onRequestSuccess: () => void
  strikePrice?: ShipperStrikePrices
}

export const StrikePriceOverrideModal: FC<IOverrideModalProps> = ({
  onClose,
  onRequestSuccess,
  strikePrice,
}) => {
  const currentOverride = strikePrice?.manualOverride
  const isEdit = currentOverride !== null
  const {
    error: createError,
    loading: createRequestPending,
    mutate: createOverride,
  } = useCreateShipperStrikePriceOverride({})
  const {
    error: updateError,
    loading: updateRequestPending,
    mutate: updateOverride,
  } = useUpdateShipperStrikePriceOverride({
    shipperStrikePriceOverrideId: currentOverride?.id || 0,
  })
  const defaultOverridePercentage =
    typeof currentOverride?.overridePercentage === 'number'
      ? floatToPercentage(currentOverride.overridePercentage)
      : ''
  const methods = useForm({
    resolver: yupResolver(StrikePriceOverrideSchema),
    defaultValues: {
      overridePercentage: defaultOverridePercentage,
      reason: currentOverride?.reason || '',
      dateRange: {
        startAt: currentOverride?.startAt || '',
        endAt: currentOverride?.endAt || '',
      },
    },
  })
  const { control, errors, handleSubmit } = methods
  const dates: Date[] = []
  if (currentOverride?.startAt) dates.push(correctForTimeZone(new Date(currentOverride.startAt)))
  if (currentOverride?.endAt) dates.push(correctForTimeZone(new Date(currentOverride.endAt)))
  const yesterday = (() => {
    const date = new Date()
    date.setDate(date.getDate() - 1)
    return date
  })()
  const addOrUpdateOverride = async (formParams: IStrikePriceOverrideSchema) => {
    const requestParams: ShipperStrikePriceOverride = {
      shipperStrikePriceId: strikePrice?.id || 0,
      overridePercentage: percentageToFloat(formParams.overridePercentage),
      reason: formParams.reason,
      startAt: formParams.dateRange.startAt,
      endAt: formParams.dateRange.endAt,
    }
    if (isEdit) {
      await updateOverride(requestParams)
    } else {
      await createOverride(requestParams)
    }
    onRequestSuccess()
  }
  const requestError = (() => {
    const error = (createError || updateError)?.data
    if (!error) return ''
    if (typeof error === 'string') return error
    return error.errors?.join(',')
  })()

  return (
    <FormModal
      open
      title={isEdit ? 'Edit Override' : 'Add Override'}
      size={5}
      onClose={onClose}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'override-modal-dismiss-button',
          action: onClose,
        },
        {
          label: 'Save',
          type: 'confirm',
          dataTest: 'override-modal-confirm-button',
          action: handleSubmit(addOrUpdateOverride),
          disabled: createRequestPending || updateRequestPending,
        },
      ]}>
      {requestError && (
        <Box mb={2}>
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={requestError} />
        </Box>
      )}
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='overridePercentage'
          TextInputProps={{
            name: 'overridePercentage',
            label: 'Adjustment Percentage',
            placeholder: '%',
            dataTest: 'override-percentage',
            invalid: !!errors.overridePercentage,
            validationMessage: errors.overridePercentage?.message,
            required: true,
            type: 'number',
          }}
        />
      </Box>
      <Box mb={2}>
        <DeprecatedControlledTextInput
          control={control}
          name='reason'
          TextInputProps={{
            name: 'reason',
            label: 'Reason',
            placeholder: 'Reason',
            dataTest: 'reason',
            invalid: !!errors.reason,
            validationMessage: errors.reason?.message,
            required: true,
          }}
        />
      </Box>
      <Box>
        <Controller
          name='dateRange'
          control={control}
          defaultValue={dates}
          render={({ onChange, value }) => {
            const changeHandler = (dateArr: Date[]) => {
              const start = dateArr[0] ? formatDate(dateArr[0]) : undefined
              const end = dateArr[1] ? formatDate(dateArr[1]) : undefined
              onChange({
                startAt: start,
                endAt: end,
              })
            }
            return (
              <RangeDatePicker
                label='Select Date(s)'
                placeholder='Double click for single date'
                minDate={yesterday}
                dateFormat='m-d-Y'
                dates={value}
                onDateChange={changeHandler}
                invalid={!!errors.dateRange?.startAt?.message || !!errors.dateRange?.endAt?.message}
                validationMessage={
                  errors.dateRange?.startAt?.message || errors.dateRange?.endAt?.message
                }
                required
                large
              />
            )
          }}
        />
      </Box>
    </FormModal>
  )
}
